/**
 * `/invite?code=…` — redeem an invite code.
 *
 * Live-validates against `POST /api/v0/invites/redeem` as the user types
 * (debounced). On valid, the `Continue` button takes the user to
 * `/sign-up?code=…` with the code prefilled.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/layout/AuthShell';
import { redeemInvite } from '../api/invites';
import { ApiCallError } from '../api/client';

export function InvitePage() {
  const [params] = useSearchParams();
  const initial = params.get('code') ?? '';
  const [code, setCode] = useState(initial);
  const [status, setStatus] = useState<
    { kind: 'idle' } | { kind: 'checking' } | { kind: 'valid'; email: string | null } | { kind: 'invalid'; reason: string }
  >({ kind: 'idle' });
  const navigate = useNavigate();
  const formatCode = (value: string) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (cleaned.length <= 4) return cleaned;
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}`;
  };

  useEffect(() => {
    if (!code || code.length < 4) {
      setStatus({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setStatus({ kind: 'checking' });
    const t = setTimeout(async () => {
      try {
        const result = await redeemInvite({ code: code.toLowerCase() });
        if (cancelled) return;
        if (result.valid) {
          setStatus({ kind: 'valid', email: result.intendedEmail });
        } else {
          setStatus({ kind: 'invalid', reason: result.reason ?? 'Code not recognized.' });
        }
      } catch (err) {
        if (cancelled) return;
        setStatus({
          kind: 'invalid',
          reason: err instanceof ApiCallError ? err.apiError.message : 'Could not verify code.',
        });
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [code]);

  return (
    <AuthShell
      topRight={
        <>
          <Link to="/waitlist">Join waitlist</Link>
          <Link to="/sign-in">Sign in</Link>
        </>
      }
    >
      <div className="auth-card">
        <div className="auth-eyebrow">
          <span className="num">03</span> · Invite
        </div>
        <h1 className="auth-title">
          Redeem your <em>code.</em>
        </h1>
        <p className="auth-sub">
          A teammate or admin sent you a code. Codes expire 30 days after issue.
        </p>

        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (status.kind === 'valid') {
              navigate(`/sign-up?code=${encodeURIComponent(code)}`);
            }
          }}
        >
          <div className="field">
            <label className="label-aux">
              <span className="label">Invite code</span>
            </label>
            <input
              className="input code-input"
              id="code"
              type="text"
              maxLength={9}
              placeholder="abcd-1234"
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(formatCode(e.target.value.trim()))}
            />
            {status.kind === 'checking' && (
              <div className="helper">
                <span className="dot" /> Checking…
              </div>
            )}
            {status.kind === 'valid' && (
              <div className="helper ok">
                <span className="dot" />{' '}
                <span>
                  Valid invite{' '}
                  {status.email && (
                    <>
                      · for <strong style={{ color: 'var(--fg-1)' }}>{status.email}</strong>
                    </>
                  )}
                </span>
              </div>
            )}
            {status.kind === 'invalid' && (
              <div className="helper err">
                <span className="dot" /> {status.reason}
              </div>
            )}
          </div>

          <div style={{ height: 4 }} />

          <button
            className="btn-primary"
            type="submit"
            disabled={status.kind !== 'valid'}
          >
            Continue <span aria-hidden>→</span>
          </button>
        </form>

        <p className="below-card">
          No code? <Link to="/waitlist">Join the waitlist</Link>
        </p>
      </div>
    </AuthShell>
  );
}
